/* Presence Box - remote collaborator cell/row/column outline. The per-participant
   colour is chosen from a fixed telepointer palette via a hue modifier class
   (see .presence-box--<hue>), so no inline styles are needed. */
.presence-box {
  position: absolute;
  display: none;
  box-sizing: border-box;
  border: var(--ds-border-width-selected, 2px) solid var(--presence-color);
  border-radius: var(--ds-radius-small, 3px);
  pointer-events: none;
  will-change: transform, width, height;
  transform-origin: 0 0;
}

/* Presence Nameplate - collaborator names shown above a presence box. Inherits
   --presence-color from its parent .presence-box.

   Hidden by default and revealed only while the box is hovered (the box carries
   `pointer-events: none` so the grid stays clickable, so hover is detected in JS
   and surfaced via the `.presence-box--hovered` modifier). Its bottom edge sits
   flush on the box's top border, sharing the left edge, so it reads as a tab. */
.presence-nameplate {
  position: absolute;
  bottom: 100%;
  left: 0;
  height: 22px;
  width: max-content;
  max-width: 240px;
  display: none;
  align-items: center;
  box-sizing: border-box;
  padding: 2px 6px;
  border-top-left-radius: var(--ds-radius-small, 3px);
  border-top-right-radius: var(--ds-radius-small, 3px);
  overflow: hidden;
  white-space: nowrap;
  font-size: 12px;
  line-height: 1;
  color: var(--ds-text-inverse, #fff);
  background-color: var(--presence-color);
  pointer-events: none;
}

.presence-box--hovered > .presence-nameplate {
  display: flex;
}

/* Square the corner the nameplate tab attaches to so the two read as one shape. */
.presence-box--hovered:has(> .presence-nameplate) {
  border-top-left-radius: 0;
}

/* Telepointer palette - one modifier per collaborator hue. Kept in sync with
   `telepointerColors` in @canvas/constants (color.background.accent.<hue>.bolder). */
.presence-box--blue {
  --presence-color: var(--ds-background-accent-blue-bolder, #1558bc);
}

.presence-box--red {
  --presence-color: var(--ds-background-accent-red-bolder, #c9372c);
}

.presence-box--purple {
  --presence-color: var(--ds-background-accent-purple-bolder, #6e5dc6);
}

.presence-box--green {
  --presence-color: var(--ds-background-accent-green-bolder, #1f845a);
}

.presence-box--teal {
  --presence-color: var(--ds-background-accent-teal-bolder, #1d7f8c);
}

.presence-box--magenta {
  --presence-color: var(--ds-background-accent-magenta-bolder, #ae4787);
}

.presence-box--orange {
  --presence-color: var(--ds-background-accent-orange-bolder, #a54800);
}

.presence-box--yellow {
  --presence-color: var(--ds-background-accent-yellow-bolder, #946f00);
}

.presence-box--lime {
  --presence-color: var(--ds-background-accent-lime-bolder, #5b7f24);
}

.presence-box.highlight-fixed-fragment-open-end {
  border-right-width: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.presence-box.highlight-scrolling-fragment-open-start {
  border-left-width: 0;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
